import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Card, Empty, Select, Space, Table, Tag, Typography } from 'antd';
import { CopyOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from 'src/constants';
import { setAppTitle } from 'src/store/app';
import { giveawaysListSelector, fetchGiveaways } from 'src/store/giveaway';
import { giveawayEmbedApi } from 'src/api';

import './giveawayEmbed.scss';

const { Text } = Typography;

const normalizeLanguage = (language) => {
  const normalized = String(language || '')
    .toLowerCase()
    .trim();

  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : DEFAULT_LANGUAGE;
};

const buildLocalEmbedUrl = (uuid, language = DEFAULT_LANGUAGE) =>
  `${window.location.origin}/${uuid}/embed?lang=${normalizeLanguage(language)}`;

export const GiveawayEmbed = () => {
  const { t, i18n } = useTranslation('giveaway_embed');
  const dispatch = useDispatch();

  const giveaways = useSelector(giveawaysListSelector);

  const [embeds, setEmbeds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [embedLanguage, setEmbedLanguage] = useState(() =>
    normalizeLanguage(i18n.resolvedLanguage)
  );

  const activeGiveaway = useMemo(
    () => giveaways.find((item) => item?.status === 'active') || null,
    [giveaways]
  );

  const fetchEmbeds = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await giveawayEmbedApi.fetchGiveawayEmbeds();
      const list = response?.data?.data || [];

      setEmbeds(list);
    } catch (error) {
      console.error('fetchGiveawayEmbeds[error]', error);
      toast(error?.response?.data?.message || t('errors.load_failed'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    dispatch(setAppTitle(t('title')));
    dispatch(fetchGiveaways({}));
  }, [dispatch, t]);

  useEffect(() => {
    fetchEmbeds();
  }, [fetchEmbeds]);

  const handleCreateEmbed = useCallback(async () => {
    setIsCreating(true);

    try {
      const response = await giveawayEmbedApi.createGiveawayEmbed();
      const created = response?.data?.data;

      if (created) {
        const normalized = { ...created };

        setEmbeds((prev) => {
          const filtered = prev.filter((item) => item?._id !== normalized?._id);
          return [normalized, ...filtered];
        });

        toast.success(t('success.created'));

        if (normalized.uuid) {
          await navigator.clipboard
            .writeText(buildLocalEmbedUrl(normalized.uuid, embedLanguage))
            .catch(() => {});
        }
      }
    } catch (error) {
      console.error('createGiveawayEmbed[error]', error);
      toast(error?.response?.data?.message || t('errors.create_failed'), { type: 'error' });
    } finally {
      setIsCreating(false);
    }
  }, [embedLanguage, t]);

  const handleCopyLink = useCallback(
    async (url) => {
      if (!url) return;

      try {
        await navigator.clipboard.writeText(url);
        toast.success(t('success.copied'));
      } catch (error) {
        console.error('copyEmbedUrl[error]', error);
        toast.error(t('errors.copy_failed'));
      }
    },
    [t]
  );

  const columns = [
    {
      title: t('table.title'),
      dataIndex: 'title',
      key: 'title',
      render: (value, row) => value || row?.giveawayId?.title || row?.uuid
    },
    {
      title: t('table.status'),
      key: 'status',
      width: 120,
      render: (_, row) => {
        const status = row?.effectiveStatus || row?.giveawayId?.status;

        return <Tag color={status === 'active' ? 'green' : 'default'}>{status || '-'}</Tag>;
      }
    },
    {
      title: t('table.link'),
      key: 'url',
      render: (_, row) => {
        const url = buildLocalEmbedUrl(row?.uuid, embedLanguage);

        return (
          <Space>
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyLink(url)}>
              {t('actions.copy')}
            </Button>
          </Space>
        );
      }
    },
    {
      title: t('table.created_at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '-')
    }
  ];

  const activeEmbed =
    embeds.find((item) => (item?.effectiveStatus || item?.giveawayId?.status) === 'active') ||
    embeds[0];

  return (
    <div className="giveaway-embed">
      <div className="giveaway-embed__content">
        <div className="giveaway-embed__header">
          <Button
            className="giveaway-embed__create-btn"
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateEmbed}
            loading={isCreating}
            disabled={!activeGiveaway}
          >
            {t('actions.create')}
          </Button>
        </div>

        <Card className="giveaway-embed__card" bordered={false}>
          {activeEmbed ? (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space size={12} wrap>
                <Tag color="purple" icon={<LinkOutlined />}>
                  {activeEmbed.uuid}
                </Tag>
                <Tag
                  color={
                    (activeEmbed?.effectiveStatus || activeEmbed?.giveawayId?.status) === 'active'
                      ? 'green'
                      : 'default'
                  }
                >
                  {activeEmbed?.effectiveStatus || activeEmbed?.giveawayId?.status || '-'}
                </Tag>
              </Space>

              <Space direction="vertical">
                <Text type="secondary">{t('language')}</Text>
                <Select
                  size="middle"
                  value={embedLanguage}
                  onChange={setEmbedLanguage}
                  options={SUPPORTED_LANGUAGES.map((language) => ({
                    value: language,
                    label: language.toUpperCase()
                  }))}
                  style={{ minWidth: 100 }}
                />
              </Space>

              <div className="giveaway-embed__link-row">
                <Text strong>{buildLocalEmbedUrl(activeEmbed.uuid, embedLanguage)}</Text>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() =>
                    handleCopyLink(buildLocalEmbedUrl(activeEmbed.uuid, embedLanguage))
                  }
                >
                  {t('actions.copy')}
                </Button>
              </div>
            </Space>
          ) : (
            <Empty description={t('empty.no_embed')} />
          )}
        </Card>

        <Card className="giveaway-embed__card" bordered={false}>
          <Table
            rowKey={(row) => row?._id}
            loading={isLoading}
            columns={columns}
            dataSource={embeds}
            pagination={false}
            locale={{ emptyText: t('empty.no_data') }}
          />
        </Card>
      </div>
    </div>
  );
};
